import { useEffect, useMemo, useRef, useState } from "react"

import type { CoachingPrompt } from "@/components/chat/CoachingSurface"
import type { ConversationEvent } from "@/types/conversation"
import { goalsToPrompts } from "@/utils/coaching-helpers"
import type { CoachingContent } from "@/utils/conversation-state-builder"

// The live `/chat` coaching surface is event-stream driven, exactly like replay
// — but unlike replay (which re-folds events one scrubber-index at a time), live
// events stream in and "latest wins" instantly. A 4-page framing run therefore
// arrives as a burst that would blast straight to the last card. This hook folds
// the FULL coach_dialogue_page stream into a presentation model with a buffered,
// user-paced cursor so the framing run holds on page 1 until the trainee clicks
// Continue. See docs/coaching-surface-integration.md §5a.

export type CoachingPhase =
  | "goal_selection"
  | "framing"
  | "interjection"
  | "idle"

type CoachMode = "guidance" | "interrupt"
type Surface = "auto" | "ambient"

// The pure presentation model derived from the event stream, independent of the
// (local, user-paced) framing cursor. Exported for unit testing.
export interface DerivedCoaching {
  phase: CoachingPhase
  prompts?: CoachingPrompt[]
  question?: string
  pages: (CoachingContent | string)[] // framing run, ordered by page_number
  framingTotal: number
  framingRunId?: string // event_id of the run's page 1 (cursor-reset key)
  interjectContent?: CoachingContent | string
  coachMode?: CoachMode
  ambientNote?: CoachingContent | string
  ambientNoteId?: string // event_id of the latest ambient brief (read-tracking)
}

// The full model the surface consumes, including the local cursor + handlers.
export interface LiveCoaching extends DerivedCoaching {
  cursor: number
  totalSteps: number
  hasNextPage: boolean
  content?: CoachingContent | string // the active page (framing) or interjection
  onNext: () => void
}

type Payload = Record<string, unknown>

function asContent(value: unknown): CoachingContent | string | undefined {
  if (typeof value === "string") return value
  if (value && typeof value === "object") return value as CoachingContent
  return undefined
}

function isGoalSelection(payload: Payload): boolean {
  if (payload.page_type === "goal_selection") return true
  const content = payload.content
  return (
    !!content &&
    typeof content === "object" &&
    Array.isArray((content as CoachingContent).goals) &&
    ((content as CoachingContent).goals?.length ?? 0) > 0
  )
}

// The emitter decides visibility (`surface`); older events omit it, so derive a
// default: framing / goal-selection always auto; a single coaching card uses its
// surface if present, else maps from coach_mode (interrupt ⇒ auto, else ambient).
function resolveSurface(payload: Payload): Surface {
  const surface = payload.surface
  if (surface === "auto" || surface === "ambient") return surface
  return payload.coach_mode === "interrupt" ? "auto" : "ambient"
}

function buildQuestion(
  content: CoachingContent | string | undefined,
): string | undefined {
  if (!content) return undefined
  if (typeof content === "string") return content
  const parts: string[] = []
  if (content.title) parts.push(content.title)
  if (content.instructions?.length) parts.push(...content.instructions)
  return parts.length > 0 ? parts.join(" ") : undefined
}

/**
 * Fold the ordered event stream into the coaching presentation model. Pure —
 * no React, no cursor — so it is unit-testable against burst / out-of-order /
 * duplicate / interrupt-mid-framing / ambient-then-interrupt sequences.
 */
export function deriveCoaching(
  events: ConversationEvent[] | undefined,
): DerivedCoaching {
  const model: DerivedCoaching = { phase: "idle", pages: [], framingTotal: 1 }
  if (!events?.length) return model

  for (const e of events) {
    if (e.type === "coach_dialogue_page") {
      const p = e.payload as Payload
      const content = asContent(p.content)

      if (isGoalSelection(p)) {
        model.phase = "goal_selection"
        model.prompts = goalsToPrompts(content)
        model.question = buildQuestion(content)
        // A fresh goal-selection card supersedes any earlier framing/interject.
        model.pages = []
        model.framingTotal = 1
        model.framingRunId = undefined
        model.interjectContent = undefined
        continue
      }

      const totalPages = Number(p.total_pages ?? 1)
      const pageNumber = Number(p.page_number ?? 1)

      if (totalPages > 1) {
        // A framing run. Reset the buffer on page 1 so a second run starts clean;
        // index by page_number so out-of-order / duplicate bursts still order.
        if (pageNumber === 1) {
          model.pages = []
          model.framingRunId = e.event_id
        }
        model.phase = "framing"
        model.framingTotal = totalPages
        model.coachMode = (p.coach_mode as CoachMode) ?? "guidance"
        if (content !== undefined) model.pages[pageNumber - 1] = content
        continue
      }

      // A single coaching card.
      if (resolveSurface(p) === "auto") {
        model.phase = "interjection"
        model.interjectContent = content
        model.coachMode = (p.coach_mode as CoachMode) ?? "interrupt"
      } else {
        // Ambient brief: pre-populated, revealed only on tap. Does NOT change the
        // phase (the surface stays minimised); latest ambient wins.
        model.ambientNote = content
        model.ambientNoteId = e.event_id
      }
      continue
    }

    if (e.type === "coaching_acknowledged" || e.type === "coaching_skipped") {
      // The trainee dismissed the active framing run / interrupt → collapse.
      if (model.phase === "framing" || model.phase === "interjection") {
        model.phase = "idle"
      }
    }
  }

  return model
}

/**
 * The live coaching model + a local, user-paced framing cursor. The cursor
 * resets when the conversation changes or a new framing run begins (D4), so
 * stale copy can never flash. `onNext` advances the framing cursor and emits
 * `coaching_page_advanced` (the Got-It on the last page is handled by the
 * surface's own acknowledgement path).
 */
export function useLiveCoaching(
  events: ConversationEvent[] | undefined,
  conversationId: number | undefined,
  emitEvent?: (
    type: string,
    payload: Record<string, unknown>,
    correlates?: Record<string, unknown>,
  ) => Promise<void>,
): LiveCoaching {
  const derived = useMemo(() => deriveCoaching(events), [events])

  const [cursor, setCursor] = useState(0)
  const runKeyRef = useRef<string | null>(null)
  useEffect(() => {
    const key = `${conversationId ?? ""}:${derived.framingRunId ?? ""}`
    if (runKeyRef.current !== key) {
      runKeyRef.current = key
      setCursor(0)
    }
  }, [conversationId, derived.framingRunId])

  const totalSteps = derived.phase === "framing" ? derived.framingTotal : 1
  const boundedCursor =
    derived.phase === "framing" ? Math.min(cursor, totalSteps - 1) : 0
  const hasNextPage =
    derived.phase === "framing" && boundedCursor < totalSteps - 1

  const content =
    derived.phase === "framing"
      ? derived.pages[boundedCursor]
      : derived.phase === "interjection"
        ? derived.interjectContent
        : undefined

  const onNext = () => {
    if (!hasNextPage) return
    const from = boundedCursor
    const to = boundedCursor + 1
    setCursor(to)
    void emitEvent?.("coaching_page_advanced", {
      from_page: from,
      to_page: to,
      direction: "forward",
      total_pages: totalSteps,
      engagement_required: false,
      engagement_completed: true,
    })
  }

  return {
    ...derived,
    cursor: boundedCursor,
    totalSteps,
    hasNextPage,
    content,
    onNext,
  }
}
